import React from 'react';
import { useLingui } from '@lingui/react/macro';

import CarouselRightSmall from '@rover/icons/svg/carousel-right-small.svg';
import { Box, Button, Flex, Heading, Text } from '@rover/kibble/core';
import LoadingShimmer from '@rover/kibble/patterns/LoadingShimmer';
import Row from '@rover/kibble/patterns/Row';

const NBSP = ' ';

// Paints `text` as CSS generated content so it takes the same space as the real
// copy without adding a text node that queries or screen readers pick up.
const placeholderText = (
  text: string
): { children: null; 'data-shimmer-text': string; sx: Record<string, unknown> } => ({
  children: null,
  'data-shimmer-text': text,
  sx: { '&::before': { content: 'attr(data-shimmer-text)' } },
});

type ShimmerOverProps = {
  children: React.ReactNode;
  width?: string | number;
  borderRadius?: string;
};

// Renders `children` invisibly so the shimmer takes their exact height
// (including line wrapping at narrow widths) across the full available width.
const ShimmerOver = ({
  children,
  width = '100%',
  borderRadius = '4px',
}: ShimmerOverProps): JSX.Element => (
  <Box sx={{ position: 'relative', width, maxWidth: '100%' }}>
    <Box sx={{ visibility: 'hidden' }}>{children}</Box>
    <Box sx={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 }}>
      <LoadingShimmer
        width="100%"
        height="100%"
        borderRadius={borderRadius}
        mt="0x"
        ml="0x"
        mr="0x"
      />
    </Box>
  </Box>
);

const SectionRowShimmer = ({
  label,
  sublabel,
}: {
  label: string;
  sublabel: string;
}): JSX.Element => (
  <Row
    label={
      <ShimmerOver width="fit-content">
        <Text size="200" fontWeight="semibold" {...placeholderText(label)} />
      </ShimmerOver>
    }
    sublabel={
      <ShimmerOver>
        <Text textColor="tertiary" size="100" fontWeight="regular" {...placeholderText(sublabel)} />
      </ShimmerOver>
    }
    rightItem={
      <ShimmerOver width="fit-content">
        <CarouselRightSmall />
      </ShimmerOver>
    }
  />
);

// Same container and Row geometry as ProfileSummaryPreview.
const PreviewCardShimmer = (): JSX.Element => (
  <Box
    background="primary"
    borderRadius="primary"
    variant="elevation_low"
    py="1x"
    px="3x"
    display="flex"
  >
    <Flex flex="1">
      {/* A Row inside a flex item shrinks to its content; the full-width wrapper
          gives the percentage-width placeholders something to resolve against. */}
      <Box width="100%">
        <Row
          label={
            <ShimmerOver width="60%">
              <Text fontWeight="semibold">{NBSP}</Text>
            </ShimmerOver>
          }
          leftItem={
            <LoadingShimmer width={48} height={48} borderRadius="50%" mt="0x" ml="0x" mr="0x" />
          }
          sublabel={
            <ShimmerOver width="40%">
              <Text textColor="tertiary" size="100" fontWeight="regular">
                {NBSP}
              </Text>
            </ShimmerOver>
          }
        />
      </Box>
    </Flex>
    <Flex mt="2x" mr="1x">
      <LoadingShimmer width={56} height={16} borderRadius="4px" mt="0x" ml="0x" mr="0x" />
    </Flex>
  </Box>
);

// Mirrors SitterProfileContent's default layout (management dog sitter). Rows that
// depend on the loading data, such as cat experience, can differ once it arrives.
const SitterProfileSectionsShimmer = (): JSX.Element => {
  const { t } = useLingui();

  return (
    <Box data-testid="sitter-profile-sections-shimmer" aria-hidden>
      <Box py="8x">
        <ShimmerOver>
          <Heading size="400" mb="3x" {...placeholderText(t`Sitter Profile`)} />
        </ShimmerOver>

        <ShimmerOver>
          <Text
            textColor="primary"
            size="200"
            marginBottom="6x"
            {...placeholderText(t`This will be shown to pet owners in search and on your profile.`)}
          />
        </ShimmerOver>
      </Box>

      <Box mb="6x">
        <SectionRowShimmer
          label={t`Experience`}
          sublabel={t`Highlight your experience to connect with clients.`}
        />
        <PreviewCardShimmer />
      </Box>

      <Box mb="6x">
        <SectionRowShimmer
          label={t`Skills`}
          sublabel={t`Select which special skills you can provide.`}
        />
      </Box>

      <Box mb="10x">
        <SectionRowShimmer
          label={t`What to Expect`}
          sublabel={t`Describe what a typical day looks like, your environment, and other important info.`}
        />
      </Box>

      <Box mb="4x">
        <ShimmerOver width="100%" borderRadius="999px">
          <Button variant="primary" fullWidth>
            {NBSP}
          </Button>
        </ShimmerOver>
      </Box>
    </Box>
  );
};

export default SitterProfileSectionsShimmer;
